import { Css, Palette, Tooltip, useTestIds } from "@homebound/beam";
import {
  RadialBarDatum,
  RadialBarSerie,
  RadialBarSvgProps,
  RadialBarTooltipProps,
  ResponsiveRadialBar,
} from "@nivo/radial-bar";
import { Fragment, useMemo } from "react";

type RadialBarChartProps = {
  dataset: RadialBarSerie<RadialBarDatum>[];
  colors?: Palette[];
  loadingColor?: Palette[];
  disabled?: boolean;
  tooltipValueFormat?: (value: RadialBarTooltipProps<RadialBarDatum>) => string;
  emptyStateTooltipText?: string;
} & Omit<RadialBarSvgProps<RadialBarDatum>, "data" | "isInteractive" | "width" | "height">;

export function RadialBarChart(props: RadialBarChartProps) {
  const { dataset, colors, loadingColor, disabled, tooltipValueFormat, emptyStateTooltipText, ...otherProps } = props;
  const tid = useTestIds({});
  const Chart = useMemo(
    () => (
      <ResponsiveRadialBar
        data={dataset}
        colors={disabled ? loadingColor : colors}
        isInteractive={!disabled}
        tooltip={tooltipValueFormat ? customTooltip(tooltipValueFormat) : undefined}
        theme={{
          axis: { ticks: { text: { fontSize: 10, fill: Palette.Gray700 } } },
          labels: { text: { fontSize: 10, fill: Palette.Gray700 } },
        }}
        {...otherProps}
      />
    ),
    [dataset, colors, loadingColor, disabled, tooltipValueFormat, otherProps],
  );

  return emptyStateTooltipText ? (
    <Tooltip {...tid.dataTooltip} title={emptyStateTooltipText} placement="top" children={Chart} />
  ) : (
    <Fragment children={Chart} />
  );
}

function customTooltip(tooltipValueFormat: (value: RadialBarTooltipProps<RadialBarDatum>) => string) {
  return (data: RadialBarTooltipProps<RadialBarDatum>) => (
    <div css={Css.bgGray900.white.xs.df.mt1.p1.br4.bshBasic.maxwPx(300).tal.$}>{tooltipValueFormat(data)}</div>
  );
}
